import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, Dropdown, Input, InputNumber, Modal, Segmented, Tag, Typography } from 'antd';

import FilterBuilder from '../common/FilterBuilder';
import FilterChips from '../common/FilterChips';
import { buildActionMenu } from '../common/actionMenu';
import { buildFilterFields } from '../../utils/kqlBuilder';
import { normalizeId } from '../../utils/ids';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import { useCollectionContext } from '../../contexts/CollectionContext';
import { previewReviewRules, saveReviewRules } from '../../services/reviewRules';
import {
	blankRule, CAP_BOUNDS, describeRulesError, draftsFrom, MAX_REASON, MAX_RULE_NAME, MAX_RULES,
	previewSentence, PUBLISHING_WORDS, readableNames, ruleFilterFields, rulesProblem,
} from '../../utils/reviewRules';
import { ARROW_DOWN, ARROW_UP, KEBAB, PLUS } from '../icons';

const { Text } = Typography;

const DECISION_OPTIONS = [
	{ label: 'Reject', value: 'reject' },
	{ label: 'Accept', value: 'accept' },
];

export const RULES_INTRO = 'When a submission arrives, the rules are read in order and the first one that matches decides it. A submission no rule matches waits for review, as it does now.';
export const ME_NOTE = 'A rule cannot use “me”: nobody is signed in when it runs.';

/**
 * ACCEPT AND REJECT RULES for one public form or intake link (KPR-482;
 * keepr-api docs/SCHEMA.md § Accept and reject rules; the owner's ruling
 * f06). An ordered list: each rule is a name, Accept or Reject, a condition
 * over the form's OWN answers — the list's own FilterBuilder and FilterChips,
 * offered only the elements the form asks or shows (the API refuses anything
 * else: a private question, a prefilled element, a system field, another
 * record) — and, for a reject, the reason History keeps.
 *
 *   - ORDER is the meaning: Move up / Move down in each rule's ⋯ (a short
 *     list a person arranges once; the standard's menu entries, §5).
 *   - PREVIEW asks the API what these rules — as they stand in the editor,
 *     unsaved — would do to the review queue and the last decided
 *     submissions, and where they disagree with what people decided. It
 *     writes nothing, and it is the way to try a rule before switching it on.
 *   - An ACCEPT on a collection anyone can read publishes without review: the
 *     plain-words box is asked for before Save (the API refuses without it),
 *     shown up front on a published collection and after the API says so
 *     otherwise (a live view link is the API's to know).
 *   - A rule that STOPPED working says why (the API's `broken`), until a save.
 *   - A save finishes the modal (§8); a refusal keeps it open with the reason,
 *     on the rule it names.
 *
 * `source` is `{ kind: 'form' | 'link', id }`; `definition` the form's
 * definition (an intake link's row); `review` what is saved; `cardId`, `cardsById`
 * and `getElementsForCard` the card's vocabulary.
 */
export default function ReviewRulesModal({
	open,
	onClose,
	onSaved,
	collectionId,
	source,
	name = '',
	definition = null,
	review = null,
	cardId = null,
	cardsById = {},
	getElementsForCard = null,
	published = false,
	perDay = null,
}) {
	const [rules, setRules] = useState([]);
	const [cap, setCap] = useState(CAP_BOUNDS[2]);
	const [acknowledged, setAcknowledged] = useState(false);
	const [askAcknowledgement, setAskAcknowledgement] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);
	const [preview, setPreview] = useState(null);
	const [previewing, setPreviewing] = useState(false);
	const [seedVersion, setSeedVersion] = useState(0);
	// Published (or a global collection): an accept publishes to every account.
	// The settings page's own collection, as the form editor reads it; a live
	// view link is the API's to know (it refuses, and the box is asked then).
	const contextCollection = useCollectionContext()?.collection || null;
	const ownCollection = normalizeId(contextCollection?._id) === normalizeId(collectionId) ? contextCollection : null;
	const publishedHere = published || ['published', 'retiring'].includes(ownCollection?.publication?.state) || ownCollection?.kind === 'global';

	useEffect(() => {
		if (!open) return;
		setRules(draftsFrom(review));
		const ceiling = Number.isInteger(perDay) ? Math.min(CAP_BOUNDS[1], perDay) : CAP_BOUNDS[1];
		setCap(Number.isInteger(review?.maxAutoAcceptPerDay) ? review.maxAutoAcceptPerDay : Math.min(CAP_BOUNDS[2], ceiling));
		setAcknowledged(review?.acknowledgedPublishing === true);
		setAskAcknowledgement(false);
		setError(null);
		setPreview(null);
		setSaving(false);
		setSeedVersion(version => version + 1);
	}, [open, review, perDay]);

	const state = useMemo(() => ({ rules: rules.map(({ key, broken, ...rest }) => rest), cap, acknowledged }), [rules, cap, acknowledged]);
	const { resetDirty, guardDiscard } = useDirtyForm(state, { enabled: open, baselineKey: seedVersion });
	const handleCancel = useCallback(() => guardDiscard(() => onClose?.()), [guardDiscard, onClose]);

	// The vocabulary: the form's card, narrowed to what a rule may read.
	const fields = useMemo(() => {
		const id = normalizeId(cardId);
		if (!id) return [];
		const card = cardsById?.[id] || { _id: id };
		const all = buildFilterFields({ cards: [card], cardIds: new Set([id]), getElementsForCard, keepKeys: true });
		return ruleFilterFields(all, readableNames(definition));
	}, [cardId, cardsById, getElementsForCard, definition]);

	const hasAccept = rules.some(rule => rule.decision === 'accept');
	const showAcknowledgement = hasAccept && (publishedHere || askAcknowledgement || acknowledged);
	const problem = rulesProblem(rules, cap, perDay)
		|| (hasAccept && publishedHere && !acknowledged ? 'Tick the box to say an accept rule publishes without review.' : null);

	const update = (index, patch) => setRules(prev => prev.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
	const move = (index, delta) => setRules((prev) => {
		const to = index + delta;
		if (to < 0 || to >= prev.length) return prev;
		const next = [...prev];
		const [moved] = next.splice(index, 1);
		next.splice(to, 0, moved);
		return next;
	});
	const remove = index => setRules(prev => prev.filter((_, i) => i !== index));
	const add = () => setRules(prev => (prev.length >= MAX_RULES ? prev : [...prev, blankRule()]));

	const handlePreview = useCallback(async () => {
		setPreviewing(true);
		setError(null);
		try {
			setPreview(await previewReviewRules(collectionId, source, { rules }));
		} catch (err) {
			setPreview(null);
			setError(describeRulesError(err));
		} finally {
			setPreviewing(false);
		}
	}, [collectionId, source, rules]);

	const handleSave = useCallback(async () => {
		if (problem) return;
		setSaving(true);
		setError(null);
		try {
			const result = await saveReviewRules(collectionId, source, {
				rules,
				acknowledgedPublishing: hasAccept && acknowledged,
				maxAutoAcceptPerDay: cap,
			});
			resetDirty();
			onSaved?.(result);
		} catch (err) {
			const described = describeRulesError(err);
			if (described.needsAcknowledgement) setAskAcknowledgement(true);
			setError(described);
		} finally {
			setSaving(false);
		}
	}, [problem, collectionId, source, rules, hasAccept, acknowledged, cap, resetDirty, onSaved]);

	const ruleMenu = index => buildActionMenu([
		{ key: 'up', group: 'record', icon: <ARROW_UP />, label: 'Move up', disabled: index === 0, onClick: () => move(index, -1) },
		{ key: 'down', group: 'record', icon: <ARROW_DOWN />, label: 'Move down', disabled: index === rules.length - 1, onClick: () => move(index, 1) },
		{ key: 'remove', group: 'destructive', glyph: 'remove', label: 'Remove rule', onClick: () => remove(index) },
	]);

	const renderRule = (rule, index) => {
		const flagged = error && error.index === index;
		const label = rule.name.trim() || `Rule ${index + 1}`;
		return (
			<li key={rule.key} className={['review-rules__rule', flagged ? 'review-rules__rule--flagged' : ''].filter(Boolean).join(' ')} data-testid="review-rule">
				<div className="review-rules__rule-head">
					<span className="review-rules__index" aria-hidden="true">{index + 1}</span>
					<Input
						aria-label={`Name of rule ${index + 1}`}
						maxLength={MAX_RULE_NAME}
						value={rule.name}
						placeholder="e.g. Obvious spam"
						onChange={event => update(index, { name: event.target.value })}
						className="review-rules__name"
					/>
					<Segmented
						aria-label={`What rule ${index + 1} does`}
						options={DECISION_OPTIONS}
						value={rule.decision}
						onChange={value => update(index, { decision: value })}
						className="review-rules__decision"
					/>
					<Dropdown menu={ruleMenu(index)} trigger={['click']} placement="bottomRight">
						<Button type="text" icon={<KEBAB />} aria-label={`Actions for ${label}`} />
					</Dropdown>
				</div>
				{rule.broken ? (
					<Alert
						className="review-rules__broken"
						type="warning"
						showIcon
						message="This rule stopped working"
						description={`${rule.broken.reason || 'It can no longer judge submissions.'} Until it is fixed, it and the rules after it decide nothing.`}
						data-testid="review-rule-broken"
					/>
				) : null}
				<div className="review-rules__when">
					<span className="share-links__field-label">{rule.decision === 'accept' ? 'Accept a submission when' : 'Reject a submission when'}</span>
					<div className="review-rules__when-row">
						{rule.when ? (
							<FilterChips query={rule.when} fields={fields} onChange={text => update(index, { when: `${text || ''}`.trim() })} className="review-rules__chips" />
						) : null}
						<FilterBuilder
							fields={fields}
							query={rule.when}
							onApply={text => update(index, { when: `${text || ''}`.trim() })}
							collectionId={collectionId}
							emptyText="No conditions yet — add the answers this rule looks for."
							disallowMe
							meDisallowedNote={ME_NOTE}
						/>
					</div>
				</div>
				{rule.decision === 'reject' ? (
					<div className="review-rules__reason">
						<label className="share-links__field-label" htmlFor={`review-rule-reason-${rule.key}`}>Reason kept in History (optional)</label>
						<Input
							id={`review-rule-reason-${rule.key}`}
							maxLength={MAX_REASON}
							value={rule.reason}
							placeholder="e.g. Spam"
							onChange={event => update(index, { reason: event.target.value })}
						/>
					</div>
				) : null}
			</li>
		);
	};

	const ruleName = index => (preview?.rules?.[index]?.name || rules[index]?.name || `Rule ${index + 1}`);
	const renderSample = sample => (Array.isArray(sample) && sample.length ? (
		<ul className="review-rules__sample">
			{sample.map(entry => (
				<li key={`${normalizeId(entry._id)}-${entry.decision}`}>
					<Tag color={entry.decision === 'accept' ? 'green' : 'red'}>{entry.decision === 'accept' ? 'Accept' : 'Reject'}</Tag>
					<span className="review-rules__sample-title">{entry.title || 'Untitled submission'}</span>
					<Text type="secondary" className="review-rules__sample-rule">{ruleName(entry.rule)}</Text>
					{entry.outcome ? <Text type="secondary">{entry.outcome === 'accepted' ? ' · a person accepted it' : ' · a person rejected it'}</Text> : null}
				</li>
			))}
		</ul>
	) : null);

	return (
		<Modal
			open={open}
			title={name ? `Rules for ${name}` : 'Accept and reject rules'}
			okText="Save rules"
			okButtonProps={{ disabled: Boolean(problem), 'data-action': 'review-rules-save' }}
			confirmLoading={saving}
			onOk={handleSave}
			onCancel={handleCancel}
			destroyOnClose
			width={680}
			className="share-links__modal review-rules"
		>
			<Text type="secondary" className="review-rules__intro">{RULES_INTRO}</Text>
			{!fields.length ? (
				<Alert
					className="review-rules__notice"
					type="info"
					showIcon
					message="A rule reads only what the form asks or shows, and this form has nothing a rule can read."
				/>
			) : null}

			{rules.length ? (
				<ol className="review-rules__list" aria-label="Rules, in the order they are read">{rules.map(renderRule)}</ol>
			) : (
				<p className="review-rules__empty">No rules: people review every submission.</p>
			)}
			<Button icon={<PLUS />} onClick={add} disabled={rules.length >= MAX_RULES || !fields.length} data-action="review-rules-add">
				Add a rule
			</Button>

			<div className="share-links__field review-rules__cap">
				<label className="share-links__field-label" htmlFor="review-rules-cap">Accepted by a rule, at most</label>
				<InputNumber
					id="review-rules-cap"
					min={CAP_BOUNDS[0]}
					max={Number.isInteger(perDay) ? Math.min(CAP_BOUNDS[1], perDay) : CAP_BOUNDS[1]}
					precision={0}
					value={cap}
					onChange={value => setCap(Number.isInteger(value) ? value : CAP_BOUNDS[2])}
					addonAfter="a day"
				/>
				<p className="share-links__hint">Past it, submissions an accept rule matches wait for review until the day moves on.</p>
			</div>

			{showAcknowledgement ? (
				<div className="share-links__field review-rules__acknowledge">
					<Checkbox checked={acknowledged} onChange={event => setAcknowledged(event.target.checked)} data-testid="review-rules-acknowledge">
						{PUBLISHING_WORDS} I understand.
					</Checkbox>
				</div>
			) : null}

			<div className="review-rules__preview">
				<Button onClick={handlePreview} loading={previewing} disabled={Boolean(rulesProblem(rules, cap, perDay)) && rules.length > 0} data-action="review-rules-preview">
					Preview
				</Button>
				<Text type="secondary"> What these rules would have done — nothing is changed.</Text>
				{preview ? (
					<div className="review-rules__preview-result" data-testid="review-rules-preview" aria-live="polite">
						<p><strong>Waiting for review now ({preview.pending?.total || 0}{preview.pending?.capped ? ', the newest' : ''}):</strong> {previewSentence(preview.pending)}.</p>
						{renderSample(preview.pending?.sample)}
						<p><strong>The last {preview.recent?.total || 0} decided by people:</strong> {previewSentence(preview.recent)}.</p>
						{preview.recent?.disagree?.acceptedWhatWasRejected ? (
							<p className="review-rules__disagree">Would accept {preview.recent.disagree.acceptedWhatWasRejected} that a person rejected.</p>
						) : null}
						{preview.recent?.disagree?.rejectedWhatWasAccepted ? (
							<p className="review-rules__disagree">Would reject {preview.recent.disagree.rejectedWhatWasAccepted} that a person accepted.</p>
						) : null}
						{renderSample(preview.recent?.sample)}
						{preview.pending?.broken || preview.recent?.broken ? (
							<Alert type="warning" showIcon message={`${ruleName((preview.pending?.broken || preview.recent?.broken).rule)} cannot judge: ${(preview.pending?.broken || preview.recent?.broken).reason}`} />
						) : null}
					</div>
				) : null}
			</div>

			{problem && rules.length ? <p className="share-links__hint intake-link-modal__problem" data-testid="review-rules-problem">{problem}</p> : null}
			{error ? <Alert className="intake-link-modal__error" type="error" showIcon message={error.message} data-testid="review-rules-error" /> : null}
		</Modal>
	);
}
